/**
 * Returns and statements settings that live on a buyer or supplier, in its
 * `settings` JSON beside everything else there:
 *
 * - buyer: `return_window_days` (absent: the organisation's default; 0: no
 *   returns) and `billing` (who statements are addressed to);
 * - supplier: `chargeback_returns` (absent: on) and `payee` (who supplier
 *   statements are to, VAT, bank details).
 *
 * Every other key in settings is kept as it is.
 */
import { NumberInput, SimpleGrid, Stack, Switch, Text, TextInput, Textarea } from '@mantine/core';
import { useVocab } from '../vocab';

type Settings = Record<string, unknown>;

function obj(settings: unknown): Settings {
  return settings && typeof settings === 'object' ? (settings as Settings) : {};
}

function sub(settings: unknown, key: string): Record<string, unknown> {
  const v = obj(settings)[key];
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

function text(o: Record<string, unknown>, k: string): string {
  const v = o[k];
  return typeof v === 'string' ? v : '';
}

/** A buyer's own return window, or null for the organisation's default. */
export function readReturnWindow(settings: unknown): number | null {
  const v = obj(settings).return_window_days;
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function writeReturnWindow(settings: unknown, days: number | null): Settings {
  const s = { ...obj(settings) };
  if (days == null) delete s.return_window_days;
  else s.return_window_days = Math.max(0, Math.round(days));
  return s;
}

/** Whether a supplier is charged back for a returned lead. On unless switched off. */
export function readChargeback(settings: unknown): boolean {
  return obj(settings).chargeback_returns !== false;
}

export function writeChargeback(settings: unknown, on: boolean): Settings {
  const s = { ...obj(settings) };
  if (on) delete s.chargeback_returns;
  else s.chargeback_returns = false;
  return s;
}

function writeSub(settings: unknown, key: string, patch: Record<string, unknown>): Settings {
  const next = { ...sub(settings, key), ...patch };
  for (const [k, v] of Object.entries(next)) {
    if (v === '' || v === false || v == null) delete next[k];
  }
  const s = { ...obj(settings) };
  if (Object.keys(next).length) s[key] = next;
  else delete s[key];
  return s;
}

export function BuyerReturnsFields({
  settings, onChange, disabled,
}: { settings: unknown; onChange: (next: Settings) => void; disabled?: boolean }) {
  const v = useVocab();
  const billing = sub(settings, 'billing');
  const set = (patch: Record<string, unknown>) => onChange(writeSub(settings, 'billing', patch));
  return (
    <Stack gap="xs">
      <NumberInput
        label="Return window"
        description={`Days after a sale this ${v.one('buyer')} may return it. Empty: the organisation's default. 0: no returns.`}
        suffix=" days"
        min={0}
        max={365}
        disabled={disabled}
        value={readReturnWindow(settings) ?? ''}
        onChange={(n) => onChange(writeReturnWindow(settings, typeof n === 'number' ? n : null))}
      />
      <Text size="sm" fw={500} mt="xs">Statements go to</Text>
      <SimpleGrid cols={2}>
        <TextInput label="Billing name" disabled={disabled} placeholder="The name above" value={text(billing, 'name')} onChange={(e) => set({ name: e.currentTarget.value })} />
        <TextInput label="Billing email" disabled={disabled} value={text(billing, 'email')} onChange={(e) => set({ email: e.currentTarget.value })} />
      </SimpleGrid>
      <Textarea label="Billing address" disabled={disabled} autosize minRows={2} value={text(billing, 'address')} onChange={(e) => set({ address: e.currentTarget.value })} />
      <TextInput label="Purchase order or reference" disabled={disabled} value={text(billing, 'reference')} onChange={(e) => set({ reference: e.currentTarget.value })} />
    </Stack>
  );
}

export function SupplierReturnsFields({
  settings, onChange, disabled,
}: { settings: unknown; onChange: (next: Settings) => void; disabled?: boolean }) {
  const v = useVocab();
  const payee = sub(settings, 'payee');
  const set = (patch: Record<string, unknown>) => onChange(writeSub(settings, 'payee', patch));
  return (
    <Stack gap="xs">
      <Switch
        label="Charge back returns"
        description={`When a ${v.one('buyer')}'s return of one of their ${v.many('lead')} is approved, take what that ${v.one('lead')} cost off their next statement (once per ${v.one('lead')}).`}
        disabled={disabled}
        checked={readChargeback(settings)}
        onChange={(e) => onChange(writeChargeback(settings, e.currentTarget.checked))}
      />
      <Text size="sm" fw={500} mt="xs">Statements and payment</Text>
      <SimpleGrid cols={2}>
        <TextInput label="Payee name" disabled={disabled} placeholder="The name above" value={text(payee, 'name')} onChange={(e) => set({ name: e.currentTarget.value })} />
        <TextInput label="Email" disabled={disabled} value={text(payee, 'email')} onChange={(e) => set({ email: e.currentTarget.value })} />
      </SimpleGrid>
      <Textarea label="Address" disabled={disabled} autosize minRows={2} value={text(payee, 'address')} onChange={(e) => set({ address: e.currentTarget.value })} />
      <Switch
        label="VAT registered"
        description="Their statements add VAT at 20%."
        disabled={disabled}
        checked={payee.vat_registered === true}
        onChange={(e) => set({ vat_registered: e.currentTarget.checked })}
      />
      <TextInput label="VAT number" disabled={disabled} value={text(payee, 'vat_number')} onChange={(e) => set({ vat_number: e.currentTarget.value })} />
      <Textarea label="Bank details" disabled={disabled} autosize minRows={2} value={text(payee, 'bank_details')} onChange={(e) => set({ bank_details: e.currentTarget.value })} />
    </Stack>
  );
}
